import type {
  BillingTier,
  SubscriptionDetails,
} from "schemas/generated/billing";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { AddOn } from "schemas/addOns";

import { PlanChangeConfirmationModal } from "../modals/PlanChangeConfirmationModal";
import {
  ActiveSubscriptionPricePerMonth,
  EstimatedPlanPricePerMonth,
} from "../PlanPrice";
import NextBillingDate from "../shared/NextBillingDate";
import {
  getCountOfAdditionalProjectsInSubscription,
  getCountOfAdditionalPublishedElementBundlesInSubscription,
  getCountOfAdditionalSitesInSubscription,
} from "../shared/sharedBillingUtils";
import { PlanLabel } from "../SharedBillingComponents";
import ManagePlanMenu from "./ManagePlanMenu";
import PlanSummaryBlock from "./PlanSummaryBlock";

const PlanHeader = ({
  billingTier,
  activeSubscription,
  workspaceId,
  className,
}: {
  billingTier: BillingTier;
  activeSubscription: SubscriptionDetails | undefined;
  workspaceId: string;
  className?: string;
}) => {
  const [planChangeModalOpen, setPlanChangeModalOpen] = React.useState(false);
  const isPlanEnded = Boolean(activeSubscription?.endedAt);

  let tierToUpgradeTo: BillingTier;
  if (isPlanEnded && (billingTier === "starter" || billingTier === "pro")) {
    tierToUpgradeTo = billingTier;
  } else if (billingTier === "free") {
    tierToUpgradeTo = "starter";
  } else {
    tierToUpgradeTo = "pro";
  }

  const showUpgradeCTAInPlanHeader =
    (billingTier === "free" || billingTier === "starter") &&
    !activeSubscription?.isManagedByReplo;

  const currentBillingInterval =
    activeSubscription?.billingInterval ?? "monthly";
  const currentAdditionalSites = activeSubscription
    ? getCountOfAdditionalSitesInSubscription(activeSubscription)
    : 0;
  const currentAdditionalProjects = activeSubscription
    ? getCountOfAdditionalProjectsInSubscription(activeSubscription)
    : 0;
  const currentAdditionalPublishedElementBundles =
    tierToUpgradeTo === "starter" && activeSubscription
      ? getCountOfAdditionalPublishedElementBundlesInSubscription(
          activeSubscription,
        )
      : 0;

  const addOns = AddOn.buildFromCounts({
    additionalSites: currentAdditionalSites,
    additionalProjects: currentAdditionalProjects,
    additionalPublishedElementBundles: currentAdditionalPublishedElementBundles,
  });

  return (
    <>
      <PlanSummaryBlock className={className}>
        <div className="flex justify-between items-center">
          <div className="flex gap-2 items-center">
            <PlanLabel tier={billingTier} />
            {activeSubscription && (
              <Tag size="sm" variant="v2">
                {activeSubscription?.billingInterval === "yearly"
                  ? "Billed Annually"
                  : "Billed Monthly"}
              </Tag>
            )}
          </div>
          <div className="flex gap-2">
            {showUpgradeCTAInPlanHeader && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setPlanChangeModalOpen(true);
                }}
              >
                {isPlanEnded ? "Resume Plan" : "Upgrade"}
              </Button>
            )}
            {activeSubscription && billingTier !== "free" && (
              <ManagePlanMenu
                workspaceId={workspaceId}
                activeSubscription={activeSubscription}
              />
            )}
          </div>
        </div>
        <div className="flex gap-1 items-baseline">
          <PlanHeaderPrice activeSubscription={activeSubscription} />
          {activeSubscription && billingTier !== "free" && (
            <span className="text-xs text-muted">
              • {activeSubscription.endedAt ? "Ends" : "Renews"}{" "}
              <NextBillingDate
                workspaceId={workspaceId}
                activeSubscription={activeSubscription}
                isChosenPlanOnYearlyInterval={
                  activeSubscription.billingInterval === "yearly"
                }
                className=""
              />
            </span>
          )}
        </div>
      </PlanSummaryBlock>
      <PlanChangeConfirmationModal
        open={planChangeModalOpen}
        onOpenChange={setPlanChangeModalOpen}
        newTier={tierToUpgradeTo}
        newBillingInterval={currentBillingInterval}
        addOns={addOns}
      />
    </>
  );
};

const PlanHeaderPrice = ({
  activeSubscription,
}: {
  activeSubscription: SubscriptionDetails | undefined;
}) => {
  if (activeSubscription) {
    return (
      <ActiveSubscriptionPricePerMonth
        activeSubscription={activeSubscription}
        variant="subheader"
      />
    );
  }

  // NOTE (Cole 2025-11-24): We know they're not paying if there's no active subscription
  return (
    <EstimatedPlanPricePerMonth
      billingTier="free"
      billingInterval="monthly"
      addOns={[]}
      variant="subheader"
    />
  );
};

export default PlanHeader;
